import {motion} from 'framer-motion';
import {
  Button,
  ButtonGroup,
  closeCurrentDialog,
  DataStatus,
  DialogFooter,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  showNotification,
  useDialogRequestClose,
} from 'platform/components';
import {Box, Grid, Heading, Icon, Text, VStack} from 'platform/foundation';

import {useState} from 'react';
import {DeepPartial} from 'react-hook-form';

import {isNotNil} from 'ramda';
import {isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {
  SecondaryRecordId,
  useSendCustomerEmailMutation,
  useSendCustomerEmailReplyMutation,
} from '@dms/api/core';
import {useGetCustomerV2Query} from '@dms/api/customer';
import {EntityResourceIds} from '@dms/api/shared';
import i18n from '@dms/i18n';

import {Nullish} from 'shared/web';

import {useEmailBodyImageUpload} from '../../hooks/useEmailBodyImageUpload';
import {handleApiError} from '../../utils/handleApiError';
import {hasMessageChanged} from '../../utils/hasMessageChanged';
import {isInvalidEmail} from '../../utils/isInvalidEmail';
import {plainTextToEmailHtml} from '../../utils/plainTextToEmailHtml';
import {refreshDmsDatagrid} from '../../utils/refreshDmsDatagrid';
import {toEmailBodyPayload} from '../../utils/toEmailBodyPayload';
import {UnsavedMessageCloseGuard} from '../UnsavedMessageCloseGuard/UnsavedMessageCloseGuard';
import {CustomerSelectField} from './components/CustomerSelectField';
import {EmailPreview} from './components/EmailPreview';
import {MessageFormField} from './components/MessageFormField';
import {MessageUpload} from './components/MessageUpload';
import {useSmtpOptions} from './hooks/useSmtpOptions';
import {ReplyTarget} from './types/ReplyTarget';
import {ReplyToMessage} from './types/ReplyToMessage';
import {
  FormValues,
  SendCustomerEmailFormSchema,
  SendCustomerEmailReplyFormSchema,
} from './types/SendCustomerEmailFormSchema';
import {UploadedCustomerMessageFile} from './types/UploadedCustomerMessageFile';
import {getCustomerEmails} from './utils/getCustomerEmails';
import {toReplySubject} from './utils/toReplySubject';

export interface SentEmailToCustomerFormProps {
  isBasicMessagingOnly?: boolean;
  // SHOULD be always present, but form can function without them as "general send an email action"
  customerId?: string | Nullish;
  recordId?: string | Nullish;
  resourceId?: EntityResourceIds | Nullish;
  secondaryRecordIds?: SecondaryRecordId[] | Nullish;
  vehicleId?: string | Nullish;
  defaultValues?: {
    attachments?: UploadedCustomerMessageFile[];
    customerEmailAddress?: string | Nullish;
    subject?: string | Nullish;
    body?: string | Nullish;
  };
  onContentChange?: (content: {body: string; subject: string}) => void;
  onSent?: (content: {body: string; subject: string}) => void;
  /** The customer e-mail this message answers; opens the AI composer on the reply right away. */
  replyTo?: ReplyToMessage | Nullish;
  /** Switches to true-reply mode; replyTo is ignored when this target is set. */
  reply?: ReplyTarget | Nullish;
}

const getPreferredAddresses = (reply: ReplyTarget) =>
  reply.direction === 'INCOMING'
    ? reply.recipientAddresses
    : [reply.senderAddress].filter(isNotNil);

export function SendCustomerEmailForm(props: SentEmailToCustomerFormProps) {
  const isReplyMode = isNotNil(props.reply);
  const parentMessage = isNotNil(props.reply) ? props.reply.parentMessage : props.replyTo;
  const preferredAddresses = isNotNil(props.reply) ? getPreferredAddresses(props.reply) : [];
  const {defaultSmtpCredentialId, isLoading: isLoadingSmtpOptions} = useSmtpOptions(
    'customer-message',
    preferredAddresses
  );

  const {
    data: customer,
    isLoading: isLoadingCustomer,
    isError: isCustomerError,
  } = useGetCustomerV2Query(
    {
      customerId: props.customerId ?? '',
    },
    {skip: isNilOrEmpty(props.customerId)}
  );

  const requestClose = useDialogRequestClose();
  const [sendCustomerMessage] = useSendCustomerEmailMutation();
  const [sendCustomerEmailReply] = useSendCustomerEmailReplyMutation();
  const emailBodyUpload = useEmailBodyImageUpload();
  const [uploadedFiles, setUploadedFiles] = useState<UploadedCustomerMessageFile[]>(
    props.defaultValues?.attachments ?? []
  );

  const bodyHtml = plainTextToEmailHtml(props.defaultValues?.body);

  // A reply goes back to the address the customer wrote from, but only when the customer record
  // knows it: an inbound mail from a stranger's address must not receive the customer's facts.
  const knownEmails = getCustomerEmails(customer).map((email) => email.value);
  const replyAddress = knownEmails.find(
    (email) => email === parentMessage?.from && !isInvalidEmail(email)
  );

  const defaultValues: DeepPartial<FormValues> = {
    customerId: props.customerId ?? undefined,
    customerEmailAddress:
      props.defaultValues?.customerEmailAddress ??
      replyAddress ??
      knownEmails.find((email) => !isInvalidEmail(email)),
    subject: props.defaultValues?.subject ?? toReplySubject(parentMessage?.subject),
    body: isNilOrEmpty(bodyHtml) ? undefined : bodyHtml,
    customerMessageId: ['customer-message'],
    smtpCredentialsId: defaultSmtpCredentialId,
  };

  const handleSubmit: FormSubmitHandler<FormValues> = async (
    data,
    _setErrors,
    _reset,
    blockerContext
  ) => {
    if (!emailBodyUpload.ensureBodyReadyToSend(data.body, blockerContext)) {
      return;
    }

    const attachments = isNotNilOrEmpty(uploadedFiles)
      ? uploadedFiles.map((file) => ({
          fileId: file.fileId,
          convertToPdf: file.shouldConvertToPdf,
        }))
      : undefined;

    const request = isNotNil(props.reply)
      ? sendCustomerEmailReply({
          sendCustomerEmailReplyRequestBody: {
            ...toEmailBodyPayload(data.body),
            emailMessageId: props.reply.emailMessageId,
            subject: data.subject,
            customerId: data.customerId,
            customerEmailAddress: data.customerEmailAddress,
            copyTo: data.copyTo,
            attachments,
            recordId: props.recordId ?? null,
            resourceId: props.resourceId ?? null,
            secondaryRecordIds: props.secondaryRecordIds ?? undefined,
            emailCredentialsId: data.smtpCredentialsId,
          },
        })
      : sendCustomerMessage({
          sendCustomerEmailRequestBody: {
            ...toEmailBodyPayload(data.body),
            subject: data.subject ?? '',
            customerId: data.customerId,
            customerEmailAddress: data.customerEmailAddress,
            copyTo: data.copyTo,
            attachments,
            recordId: props.recordId ?? null,
            resourceId: props.resourceId ?? null,
            secondaryRecordIds: props.secondaryRecordIds ?? undefined,
            smtpCredentialsId: data.smtpCredentialsId,
          },
        });
    await request
      .unwrap()
      .then(() => showNotification.success(i18n.t('entity.customer.labels.successfullySend')))
      .then(() => refreshDmsDatagrid('messaging-customer-message-history'))
      // The callback carries the HTML the API received; a text consumer converts at render time.
      .then(() => props.onSent?.({body: data.body ?? '', subject: data.subject ?? ''}))
      .then(closeCurrentDialog)
      .catch(handleApiError);
  };

  const handleContentChange = (values: FormValues) =>
    props.onContentChange?.({body: values.body ?? '', subject: values.subject ?? ''});

  const isLoading = isLoadingCustomer || isLoadingSmtpOptions;

  return (
    <DataStatus isLoading={isLoading} isError={isCustomerError}>
      <Box>
        <motion.div>
          <Form<FormValues>
            defaultValues={defaultValues}
            experimentalZodSchema={
              isReplyMode ? SendCustomerEmailReplyFormSchema : SendCustomerEmailFormSchema
            }
            onChange={handleContentChange}
            onSubmit={handleSubmit}
          >
            {(control, formApi) => (
              <>
                <UnsavedMessageCloseGuard
                  hasUnsavedChanges={() =>
                    hasMessageChanged(
                      {
                        subject: formApi.getValues('subject'),
                        body: formApi.getValues('body'),
                        attachmentCount: uploadedFiles.length,
                      },
                      {
                        subject: defaultValues.subject,
                        body: defaultValues.body,
                        attachmentCount: props.defaultValues?.attachments?.length ?? 0,
                      },
                      true
                    )
                  }
                  data-testid="sendCustomerEmail"
                />
                <Grid columns={[1, 1, 2]}>
                  <VStack spacing={2}>
                    <Box paddingHorizontal={[4, 4, 10]}>
                      <VStack spacing={4} align="center" justify="center">
                        <Icon
                          value="action/mail_upload"
                          color="palettes.blue.30.100"
                          isNotFilled
                          size={20}
                        />

                        <Heading size={3}>
                          {isReplyMode
                            ? i18n.t('entity.customer.labels.replyToCustomerMessageTitle')
                            : i18n.t('entity.customer.labels.customer_message_title')}
                        </Heading>
                        <Text color="secondary" align="center">
                          {isReplyMode
                            ? i18n.t('entity.customer.labels.replyToCustomerMessageDescription')
                            : i18n.t('entity.customer.labels.customer_message_title_description')}
                        </Text>
                      </VStack>
                    </Box>

                    <CustomerSelectField
                      control={control}
                      formApi={formApi}
                      customerMessageId="customer-message"
                      preferredEmail={props.defaultValues?.customerEmailAddress ?? replyAddress}
                    />

                    <VStack spacing={4}>
                      <FormField
                        control={control}
                        name="subject"
                        type="text"
                        label={i18n.t('general.labels.subject')}
                        isRequired
                      />

                      <MessageFormField
                        isBasicMessagingOnly={props.isBasicMessagingOnly}
                        control={control}
                        formApi={formApi}
                        replyTo={parentMessage}
                        isAiComposerInitiallyOpen={!isReplyMode && isNotNil(props.replyTo)}
                        resourceId={props.resourceId}
                        recordId={props.recordId}
                        preferredLocale={customer?.preferredLocale}
                        upload={emailBodyUpload}
                      />
                      <MessageUpload
                        vehicleId={props.vehicleId}
                        resourceId={props.resourceId}
                        recordId={props.recordId}
                        uploadedFiles={uploadedFiles}
                        setUploadedFiles={setUploadedFiles}
                      />
                    </VStack>
                  </VStack>

                  <EmailPreview formApi={formApi} control={control} reply={props.reply} />
                </Grid>

                <DialogFooter>
                  <ButtonGroup align="right">
                    <Button
                      onClick={requestClose}
                      variant="secondary"
                      title={i18n.t('general.actions.cancel')}
                    />

                    <FormButton
                      control={control}
                      type="submit"
                      title={i18n.t('general.actions.send')}
                    />
                  </ButtonGroup>
                </DialogFooter>
              </>
            )}
          </Form>
        </motion.div>
      </Box>
    </DataStatus>
  );
}
